import { createEmptyTool, FIELD_CONFIG } from "./field-config";
import type { FieldValues, ToolDependency } from "./types";

const TOOL_FIELD_KEYS = new Set(["type", "value", "description", "transport", "url"]);

const YAML_DEPENDENCIES_SECTION = "dependencies";
const YAML_TOOLS_KEY = "tools:";

const unquoteYaml = (raw: string): string => {
  const trimmed = raw.trim();
  const isQuoted =
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"));
  return isQuoted ? trimmed.slice(1, -1) : trimmed;
};

const assignToolField = (tool: ToolDependency, key: string, val: string) => {
  if (TOOL_FIELD_KEYS.has(key)) {
    Object.assign(tool, { [key]: val });
  }
};

const parseToolLines = (lines: string[]): ToolDependency[] => {
  const tools: ToolDependency[] = [];
  let current: ToolDependency | null = null;

  for (const raw of lines) {
    const trimmed = raw.trim();
    const isNewItem = trimmed.startsWith("- ");

    if (isNewItem) {
      if (current) {
        tools.push(current);
      }
      current = createEmptyTool();
    }

    if (!current) {
      continue;
    }

    const line = isNewItem ? trimmed.slice(2) : trimmed;
    const colonIdx = line.indexOf(":");
    if (colonIdx !== -1) {
      assignToolField(
        current,
        line.slice(0, colonIdx).trim(),
        unquoteYaml(line.slice(colonIdx + 1)),
      );
    }
  }

  if (current) {
    tools.push(current);
  }

  return tools;
};

export const parseOpenaiYaml = (
  content: string,
): { fields: FieldValues; tools: ToolDependency[] } => {
  const fields: FieldValues = {};
  const toolLines: string[] = [];
  let currentSection = "";
  let inToolsArray = false;

  for (const line of content.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#")) {
      continue;
    }

    const isTopLevel = !line.startsWith(" ") && !line.startsWith("\t");
    if (isTopLevel && trimmed.endsWith(":") && !trimmed.includes(": ")) {
      currentSection = trimmed.slice(0, -1);
      inToolsArray = false;
      continue;
    }

    if (currentSection === YAML_DEPENDENCIES_SECTION && trimmed === YAML_TOOLS_KEY) {
      inToolsArray = true;
      continue;
    }

    if (inToolsArray) {
      toolLines.push(trimmed);
      continue;
    }

    const colonIdx = trimmed.indexOf(":");
    if (colonIdx === -1) {
      continue;
    }

    const key = trimmed.slice(0, colonIdx).trim();
    const rawVal = trimmed.slice(colonIdx + 1).trim();
    const fullKey = currentSection ? `${currentSection}.${key}` : key;

    if (rawVal === "true" || rawVal === "false") {
      fields[fullKey] = rawVal === "true";
    } else {
      fields[fullKey] = unquoteYaml(rawVal);
    }
  }

  return { fields, tools: parseToolLines(toolLines) };
};

const quoteYamlString = (value: string): string => {
  if (value.includes('"') || value.includes(":") || value.includes("#")) {
    return `'${value.replace(/'/g, "''")}'`;
  }
  return `"${value}"`;
};

const POLICY_ALLOW_IMPLICIT_KEY = "allow_implicit_invocation";

export const serializeOpenaiYaml = (fields: FieldValues, tools: ToolDependency[]): string => {
  const sections: Record<string, string[]> = {};

  for (const config of FIELD_CONFIG) {
    const value = fields[config.yamlKey];
    if (value === undefined || value === "" || value === config.defaultValue) {
      continue;
    }

    const key = config.yamlKey.split(".").slice(1).join(".");
    const { section } = config;
    if (!sections[section]) {
      sections[section] = [];
    }

    if (typeof value === "boolean") {
      sections[section].push(`  ${key}: ${value}`);
    } else {
      sections[section].push(`  ${key}: ${quoteYamlString(String(value))}`);
    }
  }

  if (!sections.policy) {
    sections.policy = [];
  }
  const hasPolicy = sections.policy.some(l => l.includes(POLICY_ALLOW_IMPLICIT_KEY));
  if (!hasPolicy) {
    const val = fields["policy.allow_implicit_invocation"];
    sections.policy.push(`  ${POLICY_ALLOW_IMPLICIT_KEY}: ${val ?? true}`);
  }

  const parts: string[] = [];

  if (sections.interface?.length) {
    parts.push(`interface:\n${sections.interface.join("\n")}`);
  }

  if (sections.policy?.length) {
    parts.push(`policy:\n${sections.policy.join("\n")}`);
  }

  if (tools.length > 0) {
    const toolLines = tools.map(tool => {
      const entries = [
        `    type: ${quoteYamlString(tool.type)}`,
        `    value: ${quoteYamlString(tool.value)}`,
      ];
      if (tool.description) {
        entries.push(`    description: ${quoteYamlString(tool.description)}`);
      }
      if (tool.transport) {
        entries.push(`    transport: ${quoteYamlString(tool.transport)}`);
      }
      if (tool.url) {
        entries.push(`    url: ${quoteYamlString(tool.url)}`);
      }
      return `  - ${entries[0].trim()}\n${entries.slice(1).join("\n")}`;
    });
    parts.push(`dependencies:\n  tools:\n${toolLines.join("\n")}`);
  }

  return `${parts.join("\n\n")}\n`;
};
